import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle,
    faChartLine,
    faEye,
    faGift
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsOfertas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Métricas de Ofertas
                    </h1>
                    <p className="text-xl text-gray-600">
                        Analiza el rendimiento de tus ofertas promocionales para optimizar tus campañas y aumentar las conversiones.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder a las Métricas de Ofertas:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, selecciona <strong>"Marketing"</strong></li>
                        <li>Haz clic en <strong>"Métricas de Ofertas"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/ofertas/metrics</code></li>
                    </ol>
                </div>

                {/* Filtros */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Filtros y Selección
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            En la parte superior de la página encontrarás un panel de filtros que te permite personalizar la vista de las métricas.
                        </p>

                        {/* Selector de Marca */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Selector de Marca
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Permite filtrar las métricas para mostrar solo las ofertas de una marca específica:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Todas las marcas:</strong> Muestra métricas agregadas de todas tus marcas</li>
                                    <li><strong>Marca específica:</strong> Muestra solo las ofertas de la marca seleccionada</li>
                                </ul>
                            </div>
                        </div>

                        {/* Botón Actualizar */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Botón Actualizar
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700">
                                    Haz clic en el botón <strong>"Actualizar"</strong> para refrescar las métricas con los datos más recientes después de cambiar el filtro de marca.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Métricas Principales */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Métricas Principales
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            En la parte superior verás cuatro tarjetas con las métricas principales agregadas:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faTag} className="mr-2 text-blue-600" />
                                    Total Ofertas
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Número total de ofertas creadas (activas, pausadas, finalizadas y borradores).
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Tener una visión general del volumen de ofertas que gestionas.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600" />
                                    Total Usos
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Suma total de veces que todas las ofertas han sido utilizadas por los clientes.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Medir el nivel de participación y aceptación de tus ofertas.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faEye} className="mr-2 text-purple-600" />
                                    Total Visualizaciones
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Suma total de veces que los clientes han visto tus ofertas.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Evaluar el alcance y la visibilidad de tus campañas promocionales.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-orange-600" />
                                    Tasa de Conversión
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Porcentaje calculado como: (Total Usos ÷ Total Visualizaciones) × 100
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Medir la efectividad de tus ofertas en convertir visualizaciones en usos reales.
                                </p>
                            </div>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Una tasa de conversión alta indica que tus ofertas son atractivas y relevantes para tus clientes. Si es baja, considera revisar el diseño, el valor de la oferta o el targeting.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Métricas Detalladas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Métricas Detalladas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Además de las métricas principales, encontrarás análisis detallados organizados en secciones:
                        </p>

                        {/* Top 5 Ofertas */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Top 5 Ofertas por Uso
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Lista de las cinco ofertas más utilizadas, ordenadas por número de usos:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Nombre de la oferta</li>
                                    <li>Número total de usos</li>
                                    <li>Número de visualizaciones</li>
                                    <li>Tasa de conversión individual</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Útil para:</strong> Identificar qué tipos de ofertas funcionan mejor y replicar estrategias exitosas.
                                </p>
                            </div>
                        </div>

                        {/* Distribución por Estado */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Distribución por Estado
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Visualización de cuántas ofertas hay en cada estado:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Borrador:</strong> Ofertas creadas pero aún no publicadas</li>
                                    <li><strong>Activa:</strong> Ofertas actualmente disponibles para los clientes</li>
                                    <li><strong>Pausada:</strong> Ofertas temporalmente desactivadas</li>
                                    <li><strong>Finalizada:</strong> Ofertas que han cumplido su período de vigencia</li>
                                    <li><strong>Cancelada:</strong> Ofertas canceladas antes de finalizar</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Útil para:</strong> Gestionar el ciclo de vida de tus ofertas y mantener un equilibrio saludable entre ofertas activas y pausadas.
                                </p>
                            </div>
                        </div>

                        {/* Distribución por Tipo */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Distribución por Tipo
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Visualización de cuántas ofertas hay de cada tipo:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Descuento Porcentual:</strong> Ofertas con descuento en porcentaje</li>
                                    <li><strong>Descuento Fijo:</strong> Ofertas con descuento en cantidad fija</li>
                                    <li><strong>2x1:</strong> Ofertas de compra uno y lleva dos</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Útil para:</strong> Entender qué tipos de ofertas prefieres usar y evaluar su efectividad relativa.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interpretación de Datos */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Interpretación de los Datos
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para aprovechar al máximo las Métricas de Ofertas:
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Tasa de Conversión:</strong> Una tasa superior al 5-10% generalmente se considera buena. Si es menor, considera mejorar el diseño de la oferta, aumentar el valor del descuento o mejorar el targeting.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Top 5 Ofertas:</strong> Analiza qué tienen en común las ofertas más exitosas (tipo, valor del descuento, período de vigencia) y aplica esos patrones a nuevas ofertas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Distribución por Estado:</strong> Mantén un equilibrio saludable. Demasiadas ofertas activas pueden diluir el impacto, mientras que muy pocas pueden reducir las oportunidades de conversión.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Visualizaciones vs Usos:</strong> Si tienes muchas visualizaciones pero pocos usos, puede indicar que las ofertas no son suficientemente atractivas o que hay barreras para su uso.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Optimización */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Optimización de Ofertas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Basándote en las métricas, puedes optimizar tus ofertas:
                        </p>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <span className="text-blue-500 mr-3 font-bold">1.</span>
                                <div>
                                    <strong className="text-gray-800">Aumenta el valor:</strong> Si la tasa de conversión es baja, considera aumentar el porcentaje o monto del descuento.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-500 mr-3 font-bold">2.</span>
                                <div>
                                    <strong className="text-gray-800">Mejora el diseño:</strong> Ofertas visualmente atractivas y claras tienden a tener mejor rendimiento.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-500 mr-3 font-bold">3.</span>
                                <div>
                                    <strong className="text-gray-800">Ajusta el timing:</strong> Analiza cuándo se usan más las ofertas y programa nuevas ofertas en esos momentos.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-500 mr-3 font-bold">4.</span>
                                <div>
                                    <strong className="text-gray-800">Segmenta mejor:</strong> Dirige ofertas específicas a segmentos de clientes que más las valorarán.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-500 mr-3 font-bold">5.</span>
                                <div>
                                    <strong className="text-gray-800">Replica el éxito:</strong> Usa las características de las ofertas del Top 5 como base para nuevas campañas.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No se muestran métricas</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de tener ofertas creadas en tu marca. Si acabas de crear tu cuenta, es normal que las métricas estén en cero hasta que crees y publiques ofertas.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">La tasa de conversión muestra 0%</h4>
                                <p className="text-gray-700 text-sm">
                                    Esto puede ocurrir si tus ofertas tienen visualizaciones pero aún no han sido utilizadas. Asegúrate de que las ofertas estén activas y sean visibles para los clientes.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Los datos no se actualizan</h4>
                                <p className="text-gray-700 text-sm">
                                    Haz clic en el botón "Actualizar" después de cambiar el filtro de marca. Los datos se calculan en tiempo real, pero puede haber un pequeño retraso.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/analytics/tiendas"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Estadísticas de Tiendas
                    </Link>
                    <Link
                        to="/documentation/analytics/reporting"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Siguiente: Centro de Reporting
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsOfertas;

